import { useState } from 'react';
import { MapPin, Building2, Navigation } from 'lucide-react';
import MapPicker from './MapPicker';
import PlaceAutocompleteInput from './PlaceAutocompleteInput';

export default function BookingLocationPicker({ 
  label = "Pickup Location",
  value,
  onChange,
  businessLocation,
  showMap = false,
  onToggleMap
}) {
  const [mode, setMode] = useState(value?.label ? 'custom' : 'business');
  
  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 'business' && businessLocation) {
      onChange({
        label: businessLocation.business_map_label || 'Business Location',
        address: businessLocation.business_address,
        lat: businessLocation.business_lat,
        lng: businessLocation.business_lng,
        notes: ''
      });
    } else if (newMode === 'custom') {
      onChange({
        label: '',
        address: '',
        lat: null,
        lng: null,
        notes: ''
      });
    }
  };

  const handleFieldChange = (field, val) => {
    onChange({ ...value, [field]: val });
  };

  const handleMapChange = (lat, lng) => {
    onChange({ ...value, lat, lng });
  };

  const handlePlaceSelected = (place) => {
    if (!place) {
      onChange({ ...value, label: '', address: '', lat: null, lng: null, place_id: null, osm_id: null, provider: null, manual: false });
      return;
    }
    onChange({
      ...value,
      label: value?.label || (place.address?.suburb || place.address?.city || place.display_name?.split(',')[0]) || '',
      address: place.address,
      lat: place.lat,
      lng: place.lng,
      place_id: place.place_id,
      osm_id: place.osm_id,
      provider: place.provider,
      manual: place.manual,
    });
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium">{label}</label>
      
      {/* Mode Selector */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => handleModeChange('business')}
          className={`flex-1 px-3 py-2 rounded-lg border text-sm inline-flex items-center justify-center gap-2 ${
            mode === 'business' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <Building2 className="w-4 h-4" />
          Business Base
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('custom')}
          className={`flex-1 px-3 py-2 rounded-lg border text-sm inline-flex items-center justify-center gap-2 ${
            mode === 'custom' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <MapPin className="w-4 h-4" />
          Custom Location
        </button>
      </div>

      {/* Custom Location Fields */}
      {mode === 'custom' && (
        <>
          <div>
            <input
              type="text"
              placeholder="Short label (e.g., Customer Hotel, Airport)"
              value={value?.label || ''}
              onChange={(e) => handleFieldChange('label', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              data-testid="blp-label"
            />
          </div>
          {/* Place autocomplete (replaces the old free-text Address input). */}
          <PlaceAutocompleteInput
            label={null}
            placeholder="Search address: start typing a place (e.g., Carol-an Ayungon, Dumaguete Airport)"
            value={value?.lat ? {
              label: typeof value?.address === 'string' ? value.address : value?.label,
              address: value?.address,
              lat: value?.lat,
              lng: value?.lng,
              manual: value?.manual,
            } : null}
            onChange={handlePlaceSelected}
            countryCode="ph"
            testId="blp-place"
            helperText="Pick a suggestion so ConfirmPH can calculate distance correctly."
          />

          {/* Map Toggle */}
          <button
            type="button"
            onClick={onToggleMap}
            className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
          >
            <Navigation className="w-4 h-4" />
            {showMap ? 'Hide Map' : 'Set on Map'}
          </button>
          
          {showMap && (
            <MapPicker
              lat={value?.lat}
              lng={value?.lng}
              onLocationChange={handleMapChange}
              className="mt-2"
            />
          )}

          <textarea
            placeholder="Location notes (optional)"
            value={value?.notes || ''}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
            rows={2}
            data-testid="blp-notes"
          />
        </>
      )}
      
      {/* Business Location Display */}
      {mode === 'business' && businessLocation && (
        <div className="p-3 bg-gray-50 rounded-lg text-sm">
          <div className="font-medium">{businessLocation.business_map_label || 'Business Location'}</div>
          {businessLocation.business_address && (
            <div className="text-gray-600 mt-1">{businessLocation.business_address}</div>
          )}
          {businessLocation.business_lat && businessLocation.business_lng && (
            <div className="text-xs text-gray-500 mt-1">
              📍 {businessLocation.business_lat.toFixed(6)}, {businessLocation.business_lng.toFixed(6)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
